import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { getDashboardData } from './_requests';
import { DashboardQuery, DashboardResponse } from './_models';

export const useDashboardData = (query: DashboardQuery = {}, refreshInterval: number = 30000) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data,
    isLoading,
    error,
    refetch,
    isError
  } = useQuery<DashboardResponse>(
    ['dashboard-data', query],
    () => getDashboardData(query),
    {
      refetchInterval: refreshInterval, // Auto refresh every 30 seconds
      refetchOnWindowFocus: true,
      staleTime: 10000, // Data is fresh for 10 seconds
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  );

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    data: data || null,
    isLoading: isLoading || isRefreshing,
    isError,
    error,
    refresh,
    isRefreshing,
    // Derived data for easier access
    stats: data?.stats,
    userFundSummary: data?.userFundSummary,
    transactionSummary: data?.transactionSummary,
    performanceData: data?.performanceData || [],
    transactionTypeData: data?.transactionTypeData || [],
  };
};

export const useDashboardStats = (merchantId?: string, period: 'today' | 'week' | 'month' | 'year' = 'month') => {
  const today = new Date();
  let startDate: string;
  let endDate = today.toISOString().split('T')[0];

  switch (period) {
    case 'today':
      startDate = endDate;
      break;
    case 'week':
      startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      break;
    case 'month':
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()).toISOString().split('T')[0];
      break;
    case 'year':
      startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()).toISOString().split('T')[0];
      break;
    default:
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()).toISOString().split('T')[0];
  }

  return useDashboardData({
    merchantId,
    startDate,
    endDate,
    period
  });
};

export default useDashboardData;
